import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router'
import { useEffectOnce } from 'react-use'
import { ISelectOption } from 'components/forms/Selects/SelectOption'
import isEmpty from 'lodash/isEmpty'
import times from 'lodash/times'
import values from 'lodash/values'
import SortArrowDownIcon from 'transfix-icons/build/SortArrowDownIcon'
import { Card } from 'transfix-ui/components/Card'
import { Pagination } from 'transfix-ui/components/Pagination'
import { SingleSelect } from 'transfix-ui/components/SingleSelect'
import { Table, TableBody, TableCell, TableRow } from 'transfix-ui/components/Table'
import { Toast, TToastNewState, useToastState } from 'transfix-ui/components/Toast'
import { TopNav } from 'transfix-ui/components/TopNav'
import { Box, Divider, Grid, makeStyles, Typography, Skeleton } from 'transfix-ui/core'
import { calcGrid } from 'transfix-ui/theme'
import { brandColors } from 'transfix-ui/theme/colors'
import { StringParam, useQueryParams } from 'use-query-params'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { useFeatureFlagContext } from 'utils/context/FeatureFlagsContext'
import { useMarketplaceContext } from 'utils/context/MarketplaceContext'
import { BookLoadsBanner } from 'apps/fleet-planner/components/BookLoadsBanner'
import { MyBidsButton } from 'apps/fleet-planner/components/MyBidsButton'
import { SetBrowserLocationButton } from 'apps/fleet-planner/components/SetBrowserLocationButton'
import { BookableShipmentSavedSearchAlertContainer } from 'apps/fleet-planner/components/bookable-shipment-saved-search/alert/BookableShipmentSavedSearchAlertContainer'
import { FiltersContainer } from 'apps/fleet-planner/components/bookable-shipment-saved-search/filters/FiltersContainer'
import { DedicatedLanesBookLoadsResults } from 'apps/fleet-planner/components/dedicated-lanes/DedicatedLanesBookLoadsResults'
import { ChangeMyLocationModal } from 'apps/fleet-planner/components/modals/ChangeMyLocationModal'
import { SORT_OPTIONS } from 'apps/fleet-planner/constants/marketplace'
import { useUserContext } from 'apps/fleet-planner/context'
import { useLaneFiltersContext } from 'apps/fleet-planner/context/LaneFiltersContext'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { useTrackConversion } from 'apps/fleet-planner/hooks/useTrackConversion'
import {
  ITestCarrierProdBookingModal,
  TestCarrierProdBookingModal,
  isTestCarrier,
} from 'apps/fleet-planner/pages/TestCarrierProdBookingModal'
import { NoLoadsMatch } from 'apps/fleet-planner/pages/marketplace/EmptyState'
import { MarketplaceCardRow } from 'apps/fleet-planner/pages/marketplace/MarketplaceCardRow'
import { MarketplaceTableRow } from 'apps/fleet-planner/pages/marketplace/MarketplaceTableRow'
import { AuctionShipment, useBookableShipmentSearch } from 'apps/fleet-planner/types'
import { isCarrierAllowedToBookShipment } from 'apps/fleet-planner/utils/isCarrierAllowedToBookShipment'
import { Container } from 'components/layout'
import { MarketplaceLoadDetailsModalContainer } from './MarketplaceLoadDetailsContainer'

const useMarketplacePageStyles = makeStyles(theme => ({
  inner: {
    maxWidth: '100%',
    [theme.breakpoints.up('md')]: {
      maxWidth: calcGrid(10), // 83.3%
    },
  },
  verticalDivider: {
    border: `0.5px solid ${brandColors.coolGray4}`,
    height: '50%',
    marginTop: theme.spacing(2),
  },
  subtitle: {
    paddingLeft: theme.spacing(3),
  },
}))

export const MarketplacePage = memo(() => {
  const { trackEvent } = useFMSAnalytics()
  const classes = useMarketplacePageStyles()
  const history = useHistory()
  const { userData } = useUserContext()
  const { getFlag } = useFeatureFlagContext()
  const { isMobile } = useBreakpointContext()

  const {
    getMarketplaceLoad,
    getMarketplaceShipmentCarrierCompliance,
    setActiveShipmentDetailsId,
  } = useMarketplaceContext()

  const {
    changeParams,
    clearAllFilters,
    hasStartedSearch,
    params,
    queryParams: laneQueryParams,
    setHasStartedSearch,
  } = useLaneFiltersContext()

  const { applyRulesAndSetConversion } = useTrackConversion()

  const { data, loading: isLoading, refetch } = useBookableShipmentSearch({
    lazy: true,
    queryParamStringifyOptions: {
      encodeValuesOnly: true,
      arrayFormat: 'brackets',
    },
  })

  const [{ eventSource }] = useQueryParams({
    eventSource: StringParam,
  })

  const { enabled: fmsBookLoadsMobile } = getFlag('fmsBookLoadsMobile')
  const { shipmentId } = useParams<{ shipmentId: string }>()
  const { search } = useLocation()
  const { motorCarriers = [] } = userData || {}
  const { auctionShipments = [], totalCount = 0 } = data || {}
  const { filters, pageNumber = 0 } = params || {}
  const numberOfPages = Math.ceil(totalCount / 20)
  const selectedSortOption =
    SORT_OPTIONS.filter(option => option.value === params?.sortField)[0] || ''

  const hasActiveQueryParams = useMemo(() => !values(laneQueryParams).every(isEmpty), [
    laneQueryParams,
  ])
  const hasActiveFilters = useMemo(() => !values(filters).every(isEmpty), [filters])
  const shipmentsFound =
    hasActiveFilters && hasStartedSearch && !isLoading && auctionShipments.length > 0
  const noShipmentsFound =
    hasActiveFilters && hasStartedSearch && !isLoading && auctionShipments.length === 0

  const [isChangeMyLocationModalOpen, setIsChangeMyLocationModalOpen] = useState<boolean>(false)
  const { closeToast, openToast, toastMessage, toastOpen, toastType } = useToastState()

  const [testCarrierProdBookingModal, setTestCarrierProdBookingModal] = useState<
    ITestCarrierProdBookingModal
  >({
    isOpen: false,
    onSubmit: () => {},
  })

  const resetTestCarrierProdBookingModal = () => {
    setTestCarrierProdBookingModal({
      isOpen: false,
      onSubmit: () => {},
    })
  }

  const updateLocationHandler = (toastState: TToastNewState) => {
    openToast(toastState)
  }

  const openChangeMyLocationModalHandler = () => setIsChangeMyLocationModalOpen(true)

  const closeChangeMyLocationModalHandler = () => setIsChangeMyLocationModalOpen(false)

  const submitBidClickHandler = useCallback(
    (id: number) => {
      applyRulesAndSetConversion()
      history.replace(`/book/${id}/bid${search}`)
      setActiveShipmentDetailsId(id)
    },
    [applyRulesAndSetConversion, history, search, setActiveShipmentDetailsId]
  )

  // This safe version checks if the current env is production and the user belongs to a test carrier
  // If this is true, we show a warning dialog before proceeding
  const submitBidClickHandlerSafe = (id: number) => {
    if (isTestCarrier(motorCarriers)) {
      setTestCarrierProdBookingModal({
        isOpen: true,
        onSubmit: () => submitBidClickHandler(id),
      })
    } else {
      submitBidClickHandler(id)
    }
  }

  const openShipmentDetailsHandler = useCallback(
    (id: number) => {
      applyRulesAndSetConversion()
      setActiveShipmentDetailsId(id)
      history.replace(`/book/${id}${search}`)
    },
    [history, setActiveShipmentDetailsId, search, applyRulesAndSetConversion]
  )

  const bookNowClickHandler = useCallback(
    async (id: number) => {
      applyRulesAndSetConversion()
      // if user only has a single MC, run a compliance check automatically
      // open the compliance modal if they fail this check
      if (motorCarriers.length === 1) {
        const [singleCarrier] = motorCarriers || []
        const { dotNumber, mcNumber } = singleCarrier || {}

        const carrierCompliance = await getMarketplaceShipmentCarrierCompliance(
          id,
          dotNumber,
          mcNumber
        )

        const shipment = await getMarketplaceLoad(String(id))

        if (isCarrierAllowedToBookShipment(carrierCompliance, shipment)) {
          history.push(`/book/${id}/rate-confirmation`)
        } else {
          history.push(`/book/${id}/compliance`)
        }
      } else {
        history.push(`/book/${id}/rate-confirmation`)
      }
    },
    [
      applyRulesAndSetConversion,
      motorCarriers,
      getMarketplaceShipmentCarrierCompliance,
      getMarketplaceLoad,
      history,
    ]
  )

  // This safe version checks if the current env is production and the user belongs to a test carrier
  // If this is true, we show a warning dialog before proceeding
  const bookNowClickHandlerSafe = (id: number) => {
    if (isTestCarrier(motorCarriers)) {
      setTestCarrierProdBookingModal({
        isOpen: true,
        onSubmit: () => bookNowClickHandler(id),
      })
    } else {
      bookNowClickHandler(id)
    }
  }

  const sortChangeHandler = (sortField?: ISelectOption) => {
    const { value } = sortField || {}
    changeParams({
      ...params,
      sortField: value ? value.toString() : undefined,
    })
  }

  // Allow users to clear filters by selecting Book Loads link in the side nav.
  useEffect(() => {
    if (!hasActiveQueryParams && hasActiveFilters) {
      clearAllFilters()
    }
  }, [clearAllFilters, hasActiveFilters, hasActiveQueryParams])

  // Dispatch analytics events if there are search results.
  useEffect(() => {
    if (!isLoading && auctionShipments.length > 0) {
      trackEvent('Book Load', 'VIEWED_SEARCH_RESULTS_ON_BOOK_LOADS_PAGE')

      if (
        auctionShipments?.some(
          (item: AuctionShipment) => item.shipmentInfo?.pickups[0]?.appointmentType === 'FCFS'
        )
      ) {
        trackEvent('Market Load Insights', 'FCFS_WINDOW_LABEL_SHOWN_ON_BOOK_LOADS_SEARCH_RESULTS')
      }

      if (
        auctionShipments.some((item: AuctionShipment) =>
          item?.tags?.find(tag => tag.variant === 'popular_shipment')
        )
      ) {
        trackEvent(
          'Market Load Insights',
          'POPULAR_WINDOW_LABEL_SHOWN_ON_BOOK_LOADS_SEARCH_RESULTS'
        )
      }

      if (
        auctionShipments.some((item: AuctionShipment) =>
          item?.tags?.find(tag => tag.variant === 'great_rate_shipment')
        )
      ) {
        trackEvent('Market Load Insights', 'GREAT_WINDOW_LABEL_SHOWN_ON_BOOK_LOADS_SEARCH_RESULTS')
      }
    }
  }, [auctionShipments, isLoading, trackEvent])

  // If the user lands on this page with a shipment id in the url
  useEffectOnce(() => {
    trackEvent('Book Load', 'VISITED_BOOK_LOADS')
    if (shipmentId) {
      setActiveShipmentDetailsId(Number(shipmentId))
    }
    return () => setActiveShipmentDetailsId(undefined)
  })

  // Prepopulate search filters using query params
  useEffectOnce(() => {
    const { pickupCity, pickupStateCode } = laneQueryParams || {}
    if (pickupCity && pickupStateCode && !hasStartedSearch) {
      setHasStartedSearch(true)
      changeParams(
        {
          filters: {
            ...filters,
            ...laneQueryParams,
          },
        },
        true
      )
    }
  })

  // Fetch bookable shipments loads only when a filter is applied
  useEffect(() => {
    if (hasActiveFilters && hasStartedSearch) {
      trackEvent('Book Load', 'SEARCHED_BOOK_LOADS')
      refetch({ queryParams: params })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasActiveFilters, hasStartedSearch, params])

  // Clear UTM params
  useEffect(() => {
    if (eventSource === 'savedSearchEmail') {
      if (shipmentId) {
        history.push(`/book/${shipmentId}`)
      } else {
        // Remove event query param from url.
        history.push('/book')
      }
    }
  }, [eventSource, history, shipmentId])

  const pageChangeHandler = (pageCount: number) => {
    changeParams({
      ...params,
      pageNumber: pageCount - 1,
    })
  }

  const showNav = hasStartedSearch || isMobile
  const showFilters = hasStartedSearch

  return (
    <>
      {showNav && (
        <TopNav title='Find Loads' showMenu={!showFilters} showBackButton={showFilters}>
          <div className='flex'>
            {showFilters && (
              <>
                <MyBidsButton />
                {!isMobile && (
                  <Box flexGrow={1}>
                    <Divider orientation='vertical' className={classes.verticalDivider} />
                  </Box>
                )}
              </>
            )}
            {!isMobile ? (
              <SetBrowserLocationButton
                onClick={openChangeMyLocationModalHandler}
                variant='loadSearch'
              />
            ) : (
              <></>
            )}
          </div>
        </TopNav>
      )}
      <Container hidePadding>
        {!hasStartedSearch && <BookLoadsBanner />}
        <div className={`${isMobile ? classes.inner : 'ph4'}`}>
          {showFilters && <FiltersContainer />}
          {!hasStartedSearch && <BookableShipmentSavedSearchAlertContainer />}
          {hasActiveFilters && hasStartedSearch && (
            <Box pt={3}>
              <DedicatedLanesBookLoadsResults />
            </Box>
          )}
          {hasActiveFilters && hasStartedSearch && (
            <Box display='flex' alignItems='center'>
              <Typography className={classes.subtitle} variant='subtitle1'>
                Available loads
              </Typography>
              <Box ml='auto'>
                <SingleSelect
                  className='mr4 mb4'
                  id='pickup-sort'
                  // TODO: Get SVG from Josh
                  icon={<SortArrowDownIcon size='large' />}
                  label='Sort by'
                  options={SORT_OPTIONS}
                  onSelect={sortChangeHandler}
                  selected={selectedSortOption}
                  dataTest='sort'
                />
              </Box>
            </Box>
          )}

          {/* Loading state. */}
          {hasActiveFilters && isLoading && (
            <Card className='pa0 ma4'>
              <Box margin={2}>
                <Table>
                  <TableBody>
                    {times(10).map(item => (
                      <TableRow key={item}>
                        {isMobile ? (
                          /**
                           * Mobile view
                           */
                          <TableCell>
                            <Grid container>
                              <Grid item xs={12} className='pb4'>
                                <Skeleton variant='rectangular' height='60px' width='100%' />
                              </Grid>
                              <Grid item xs={12} className='pb1'>
                                <Skeleton variant='rectangular' height='70px' width='100%' />
                              </Grid>
                              <Grid item xs={12}>
                                <Skeleton variant='rectangular' height='70px' width='100%' />
                              </Grid>
                            </Grid>
                          </TableCell>
                        ) : (
                          /**
                           * Desktop view
                           */
                          <TableRow key={item}>
                            <TableCell>
                              <Skeleton variant='rectangular' height='22px' width='80px' />
                            </TableCell>
                            <TableCell>
                              <Grid container>
                                <Grid item md={6}>
                                  <Skeleton variant='rectangular' height='22px' width='160px' />
                                </Grid>
                                <Grid item md={6}>
                                  <Skeleton variant='rectangular' height='22px' width='160px' />
                                </Grid>
                              </Grid>
                            </TableCell>
                            <TableCell>
                              <Skeleton variant='rectangular' height='22px' width='120px' />
                            </TableCell>
                            <TableCell>
                              <Skeleton variant='rectangular' height='22px' width='160px' />
                            </TableCell>
                          </TableRow>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                  <></>
                </Table>
              </Box>
            </Card>
          )}
          {noShipmentsFound && <NoLoadsMatch />}
          {shipmentsFound && (
            <>
              <Card dataTest='bookable-loads-card' className='pa0 ma4'>
                <Table>
                  <TableBody>
                    {auctionShipments.map((shipment: AuctionShipment) =>
                      isMobile && fmsBookLoadsMobile ? (
                        <MarketplaceCardRow
                          onShowDetailsClick={openShipmentDetailsHandler}
                          shipment={shipment}
                        />
                      ) : (
                        <MarketplaceTableRow
                          dataTest={`marketplace-row-${shipment?.id}`}
                          onBookNowClick={bookNowClickHandlerSafe}
                          onSubmitBidClick={submitBidClickHandlerSafe}
                          key={shipment?.id}
                          id={shipment?.id}
                          shipment={shipment}
                          onShowDetailsClick={openShipmentDetailsHandler}
                        />
                      )
                    )}
                  </TableBody>
                  <></>
                </Table>
              </Card>
              {numberOfPages > 1 && (
                <div className='tc'>
                  <Pagination
                    currentPage={pageNumber + 1}
                    numberOfPages={numberOfPages}
                    onPageChange={pageChangeHandler}
                  />
                </div>
              )}
            </>
          )}
          <MarketplaceLoadDetailsModalContainer route='book' queryParams={search} showMap />
          {/**
           * This modal is conditionally rendered because it calls a useGeolocation() hook
           * which we don't want prompting the user until the modal is visible
           */}
          {isChangeMyLocationModalOpen && (
            <ChangeMyLocationModal
              isOpen={isChangeMyLocationModalOpen}
              onClose={closeChangeMyLocationModalHandler}
              onSubmitNewLocation={updateLocationHandler}
            />
          )}
          <Toast
            variation={toastType}
            open={toastOpen}
            onClose={closeToast}
            message={toastMessage}
          />
        </div>
        <TestCarrierProdBookingModal
          onClose={resetTestCarrierProdBookingModal}
          isOpen={testCarrierProdBookingModal.isOpen}
          onSubmit={testCarrierProdBookingModal.onSubmit}
        />
      </Container>
    </>
  )
})

export default MarketplacePage
